import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Pagination,
  Typography,
  Chip,
  Stack,
  Tooltip,
  alpha,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import ConfirmModal from "../../ConfirmModal/ConfirmModal";
import {
  deleteRiskType,
  getRiskTypes,
  createRiskTypes,
  updateRiskType,
} from "../../../api/services/riskProfile";
import { QK } from "../../../base/qk";
import RiskTypeDialog from "./Dialog/RiskTypeCreateDialog";
import DataTable from "../../Table/DataTable";

const RiskTypeManagement = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = useState(1);
  const { data: riskTypes, isLoading } = useQuery(
    [QK.riskTypes, page],
    () => getRiskTypes(page),
    { keepPreviousData: true }
  );

  const [openDialog, setOpenDialog] = useState(false);
  const [editingRiskType, setEditingRiskType] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });
  const [formData, setFormData] = useState({ name: "" });

  const handleOpenDialog = (riskType = null) => {
    if (riskType) {
      setEditingRiskType(riskType);
      setFormData({ name: riskType.name });
    } else {
      setEditingRiskType(null);
      setFormData({ name: "" });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRiskType(null);
    setFormData({ name: "" });
  };

  const mutationCreate = useMutation(createRiskTypes, {
    onSuccess: () => {
      enqueueSnackbar("Risk Type created successfully!", { variant: "success" });
      queryClient.invalidateQueries(QK.riskTypes);
      handleCloseDialog();
    },
    onError: () =>
      enqueueSnackbar("Failed to create risk type", { variant: "error" }),
  });

  const mutationUpdate = useMutation(
    ({ id, data }) => updateRiskType(id, data),
    {
      onSuccess: () => {
        enqueueSnackbar("Risk Type updated successfully!", { variant: "success" });
        queryClient.invalidateQueries(QK.riskTypes);
        handleCloseDialog();
      },
      onError: () =>
        enqueueSnackbar("Failed to update risk type", { variant: "error" }),
    }
  );

  const mutationDelete = useMutation(deleteRiskType, {
    onSuccess: () => {
      enqueueSnackbar("Risk Type deleted successfully!", { variant: "success" });
      queryClient.invalidateQueries(QK.riskTypes);
      setConfirmDelete({ open: false, id: null });
    },
    onError: () =>
      enqueueSnackbar("Failed to delete risk type", { variant: "error" }),
  });

  const handleSubmit = () => {
    if (!formData.name.trim())
      return enqueueSnackbar("Name is required", { variant: "warning" });

    if (editingRiskType) {
      mutationUpdate.mutate({ id: editingRiskType.id, data: formData });
    } else {
      mutationCreate.mutate(formData);
    }
  };

  const handlePageChange = (_, newPage) => setPage(newPage);

  const headers = [{ key: "name", label: "Name" }];

  const renderCell = (item, header) => {
    if (header.key === "name") {
      return (
        <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#1A1A2E" }}>
          {item.name}
        </Typography>
      );
    }
    return item[header.key] || "-";
  };

  const customActions = (item) => (
    <Stack direction="row" spacing={0.25} justifyContent="center">
      <Tooltip title="Edit" arrow>
        <IconButton
          size="small"
          onClick={() => handleOpenDialog(item)}
          sx={{
            color: "#667085",
            p: 0.5,
            "&:hover": {
              color: "#F59E0B",
              bgcolor: alpha("#F59E0B", 0.06),
            },
          }}
        >
          <EditIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete" arrow>
        <IconButton
          size="small"
          onClick={() => setConfirmDelete({ open: true, id: item.id })}
          sx={{
            color: "#667085",
            p: 0.5,
            "&:hover": {
              color: "#EF4444",
              bgcolor: alpha("#EF4444", 0.06),
            },
          }}
        >
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const totalCount = riskTypes?.count || 0;

  return (
    <Box sx={{ width: "100%", p: 3, maxWidth: 1400, mx: "auto" }}>
      <Paper
        sx={{
          borderRadius: "12px",
          border: "1px solid #E2E8F0",
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          overflow: "hidden",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "#F8FAFC",
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: alpha("#F59E0B", 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <WarningIcon sx={{ color: "#F59E0B", fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
                Risk Types
              </Typography>
              <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.7rem" }}>
                Manage risk type categories
              </Typography>
            </Box>
            <Chip
              label={`${totalCount} item${totalCount !== 1 ? "s" : ""}`}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.65rem",
                fontWeight: 600,
                bgcolor: alpha("#F59E0B", 0.08),
                color: "#F59E0B",
              }}
            />
          </Stack>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={() => handleOpenDialog()}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "6px",
              bgcolor: "#2469F0",
              px: 3,
              py: 0.75,
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 2px 8px rgba(36, 105, 240, 0.3)",
              },
            }}
          >
            Add Risk Type
          </Button>
        </Box>

        {/* TABLE */}
        <DataTable
          data={riskTypes?.results || []}
          headers={headers}
          renderCell={renderCell}
          customActions={customActions}
          loading={isLoading}
        />

        {/* PAGINATION */}
        {riskTypes?.count > 10 && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <Pagination
              count={Math.ceil(riskTypes.count / 10)}
              page={page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              size="small"
              sx={{
                "& .MuiPaginationItem-root": {
                  fontSize: "0.8rem",
                  fontWeight: 500,
                },
              }}
            />
          </Box>
        )}
      </Paper>

      <RiskTypeDialog
        open={openDialog}
        onClose={handleCloseDialog}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        isLoading={mutationCreate.isLoading || mutationUpdate.isLoading}
        editingRiskType={editingRiskType}
      />

      <ConfirmModal
        show={confirmDelete.open}
        title="Delete Risk Type"
        body="Are you sure you want to delete this risk type?"
        handleClose={() => setConfirmDelete({ open: false, id: null })}
        handleConfirm={() => mutationDelete.mutate(confirmDelete.id)}
        isLoading={mutationDelete.isLoading}
        confirmText="Delete"
        cancelText="Cancel"
        isDelete
      />
    </Box>
  );
};

export default RiskTypeManagement;